import { useCallback, type MutableRefObject } from "react";

const EPSILON = 1;

const canScrollY = (el: HTMLElement) =>
  el.scrollHeight > el.clientHeight + EPSILON;

const isScrollableOverflowY = (overflowY: string) =>
  overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

const normalizeDeltaY = (event: WheelEvent, viewportHeight: number) => {
  if (event.deltaMode === 1) return event.deltaY * 16;
  if (event.deltaMode === 2) return event.deltaY * viewportHeight;
  return event.deltaY;
};

const documentScrollElement = () =>
  (document.scrollingElement as HTMLElement | null) || document.documentElement;

/**
 * Next ancestor (or window) that can still scroll in the wheel direction.
 * Skips overflow parents that are at their own top/bottom boundary.
 */
const findChainTarget = (
  from: HTMLElement,
  deltaY: number
): HTMLElement | Window | null => {
  let parent = from.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (isScrollableOverflowY(overflowY) && canScrollY(parent)) {
      const atTop = parent.scrollTop <= EPSILON;
      const atBottom =
        parent.scrollTop + parent.clientHeight >= parent.scrollHeight - EPSILON;
      if ((deltaY > 0 && !atBottom) || (deltaY < 0 && !atTop)) {
        return parent;
      }
    }
    parent = parent.parentElement;
  }

  const se = documentScrollElement();
  const atTop = se.scrollTop <= EPSILON;
  const atBottom =
    se.scrollTop + window.innerHeight >= se.scrollHeight - EPSILON;
  if ((deltaY > 0 && !atBottom) || (deltaY < 0 && !atTop)) {
    return window;
  }
  return null;
};

const applyScroll = (target: HTMLElement | Window, deltaY: number) => {
  if (target === window) {
    window.scrollBy({ top: deltaY, left: 0, behavior: "auto" });
    return;
  }
  (target as HTMLElement).scrollTop += deltaY;
};

/**
 * When an inner panel is at its top/bottom and the user keeps scrolling,
 * forward the wheel delta to the next page/ancestor scroller so sticky-on-tab
 * can release instead of feeling stuck.
 */
export const attachScrollChain = (el: HTMLElement): (() => void) => {
  const onWheel = (event: WheelEvent) => {
    if (event.ctrlKey) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    const deltaY = normalizeDeltaY(event, el.clientHeight);
    if (deltaY === 0) return;

    const atTop = el.scrollTop <= EPSILON;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - EPSILON;

    if ((deltaY > 0 && !atBottom) || (deltaY < 0 && !atTop)) {
      return;
    }

    const target = findChainTarget(el, deltaY);
    if (!target) return;

    event.preventDefault();
    applyScroll(target, deltaY);
  };

  el.addEventListener("wheel", onWheel, { passive: false });
  return () => el.removeEventListener("wheel", onWheel);
};

/**
 * React 19 callback ref — returns cleanup when the node unmounts.
 */
export const scrollChainRef = (
  node: HTMLElement | null
): (() => void) | void => {
  if (!node) return;
  return attachScrollChain(node);
};

/**
 * Keep an object ref in sync and attach scroll chaining (React 19 callback ref).
 */
export const useScrollChainRef = <T extends HTMLElement>(
  objectRef?: MutableRefObject<T | null>
) =>
  useCallback(
    (node: T | null): (() => void) | void => {
      if (objectRef) objectRef.current = node;
      if (!node) return;
      return attachScrollChain(node);
    },
    [objectRef]
  );
